import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, ArrowRight, Loader2, AlertCircle } from 'lucide-react';
import newsletterService from '../../../services/newsletterService';

const ArchivePreviewSection = () => {
  const navigate = useNavigate();
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadLatestNewsletters();
  }, []);

  const loadLatestNewsletters = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await newsletterService?.getPublished();
      // Get only the latest 3 newsletters
      setNewsletters(data?.slice(0, 3) || []);
    } catch (err) {
      setError(err?.message || 'Failed to load newsletters');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        </div>
      </section>);

  }

  if (error) {
    return (
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-red-900">Failed to load newsletters</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      </section>);

  }

  if (newsletters?.length === 0) {
    return (
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center py-12">
            <p className="text-muted-foreground">No newsletters published yet</p>
          </div>
        </div>
      </section>);

  }

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
            Latest Newsletters
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Stay updated with our most recent newsletters featuring funding opportunities and research insights
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {newsletters?.map((newsletter) =>
          <div
            key={newsletter?.id}
            className="bg-card border border-border rounded-lg shadow-elevation-sm hover:shadow-elevation-md transition-shadow overflow-hidden group cursor-pointer"
            onClick={() => navigate(`/newsletter/${newsletter?.slug}`)}>

              <div className="p-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(newsletter?.publishedAt)}</span>
                </div>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">
                  {newsletter?.title}
                </h3>
                <p className="text-muted-foreground text-sm line-clamp-3 mb-4">
                  {newsletter?.excerpt}
                </p>
                <button
                className="inline-flex items-center gap-2 font-medium text-sm group-hover:gap-3 transition-all text-orange-50"
                onClick={(e) => {
                  e?.stopPropagation();
                  navigate(`/newsletter/${newsletter?.slug}`);
                }}>

                  Read More
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/newsletter-archive')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors">

            View All Newsletters
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>);

};

export default ArchivePreviewSection;